import React, { useEffect, useState } from 'react';
import { Table } from 'components/molecules/Table';
import { useDictionaries } from 'pages/DictionariesPage/hooks';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages, ISortOptions } from 'models/table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import { DictionaryTableCard } from 'pages/DictionariesPage/DictionaryTableCard';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Stack } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { objectToQueryString } from 'helpers/searchQuery';
import { replaceUrlIfOnPage } from 'utils/history';
import { dictionariesTableColumns, mapDictionariesToDataSource } from './helpers';
import { IDictionarySearchLike } from 'models/dictionary';
import { useForm } from 'react-hook-form';
import { FormInput } from 'components/molecules/FormInput';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Tooltip from 'components/atoms/Tooltip';
import { EFileType } from 'constants/FileType';
import { EIconTypes } from 'constants/Icons';

const DictionariesPage = () => {
  const { dictionaryAlert, nextPageAlert } = useAlert();
  const {
    dictionaries,
    getDictionariesData,
    paginator,
    downloadDictionaryTranslationTemplate,
    importDictionaryTranslationsFile,
  } = useDictionaries();
  const { t } = useTranslations();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname }: any = useLocation();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPage, setCurrentPage] = useState<number>(
    currentPageFromQuery > 0 ? currentPageFromQuery : 1,
  );
  const [perPage, setPerPage] = useState<EPerPages>(
    [25, 50, 100].includes(perPageFromQuery)
      ? (perPageFromQuery as EPerPages)
      : EPerPages.perPage25,
  );
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<IDictionarySearchLike>({
    searchValue: searchParams.get('searchValue') ?? '',
  });

  const { isDesktop } = useThemeBreakpoint();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IDictionarySearchLike>({
    defaultValues: { ...searchQuery },
  });

  useEffect(() => {
    (async function getDictionariesDataInit() {
      setIsPaginationLoading(true);
      if (!dictionaries.length) {
        setIsLoading(true);
      }
      const query = objectToQueryString({
        currentPage,
        perPage,
        ...searchQuery,
      });
      if (query.length) {
        replaceUrlIfOnPage(`${pathname}?${query}`);
      }
      await getDictionariesData(currentPage, perPage, sort, searchQuery);
      setIsPaginationLoading(false);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, sort, searchQuery]);

  const onSubmitForm = async (data: any) => {
    setCurrentPage(1);
    setSearchQuery(data);
  };

  const handleClear = () => {
    setCurrentPage(1);
    setSearchQuery({ searchValue: '' });
    setValue('searchValue', '');
    const params = new URLSearchParams(searchParams);
    params.delete('searchValue');
    setSearchParams(params);
  };

  const [isTemplateDownloadLoading, setIsTemplateDownloadLoading] = useState(false);

  const handleTemplateDownload = () => {
    setIsTemplateDownloadLoading(true);
    downloadDictionaryTranslationTemplate().then((_) => setIsTemplateDownloadLoading(false));
  };

  const [isTemplateImportLoading, setIsTemplateImportLoading] = useState(false);

  const handleTemplateImport = (file: File) => {
    setIsTemplateImportLoading(true);
    importDictionaryTranslationsFile(file).then((_) => setIsTemplateImportLoading(false));
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('dictionaries.header'),
        ]}
      />
      <StyledPageTitle variant="h4" sx={{ marginBottom: 3 }}>
        {t('dictionaries.header')}
      </StyledPageTitle>

      {dictionaryAlert && (
        <Alert text={dictionaryAlert.text} variant={dictionaryAlert.variant} />
      )}
      {nextPageAlert && <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />}

      <Grid container sx={{ display: 'flex', alignItems: 'top', marginTop: 2.5 }}>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <form onSubmit={handleSubmit(onSubmitForm)} style={{ flexGrow: 1 }}>
            <Grid
              size={{ xs: 12 }}
              sx={{
                maxWidth: { lg: '520px', md: '100%' },
                flexWrap: 'nowrap',
                display: 'flex',
              }}
            >
              <FormInput
                name="searchValue"
                control={control}
                errors={errors}
                setValue={setValue}
                withLabel={false}
                fullWidth
                placeholder={t('dictionaries.search.placeholder.text')}
                sx={{
                  '.MuiInputBase-root': {
                    height: '40px',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderBottomRightRadius: 0,
                    borderTopRightRadius: 0,
                  },
                }}
              />
              <Button
                variant={EButtonVariants.contained}
                label={t('global.search.button')}
                type="submit"
                id="dictionary-search"
                sx={{
                  borderBottomLeftRadius: 0,
                  borderTopLeftRadius: 0,
                }}
              />
            </Grid>
          </form>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6, lg: 6 }}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            {searchQuery.searchValue && (
              <span>
                <Button
                  variant={EButtonVariants.text}
                  label={t('clear.general.label')}
                  icon={EIconTypes.close}
                  size={EButtonSizes.small}
                  onClick={handleClear}
                />
              </span>
            )}
            <Tooltip title={t('dictionaries.import.tooltip.text')} childrenHeight="40px">
              <span>
                <Button
                  variant="outlined"
                  label={t('dictionaries.import.button.text')}
                  onClick={(files) => handleTemplateImport(files[0])}
                  sx={{ minWidth: '95px' }}
                  isUploadButton
                  uploadButtonAcceptExtensions={EFileType.XLSX}
                  isLoading={isTemplateImportLoading}
                  disabled={isTemplateImportLoading}
                />
              </span>
            </Tooltip>
            <Tooltip title={t('dictionaries.export.tooltip.text')} childrenHeight="40px">
              <span>
                <Button
                  variant="outlined"
                  label={t('dictionaries.export.button.text')}
                  onClick={() => handleTemplateDownload()}
                  isLoading={isTemplateDownloadLoading}
                  disabled={isTemplateDownloadLoading}
                />
              </span>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>

      {isDesktop ? (
        <Table
          columns={dictionariesTableColumns({
            t,
            query: searchQuery.searchValue,
          })}
          dataSource={mapDictionariesToDataSource(dictionaries)}
          onSort={setSort}
          loading={isLoading}
          loader
        />
      ) : (
        <CardList
          items={mapDictionariesToDataSource(dictionaries)}
          render={(data, index) => (
            <DictionaryTableCard data={data} query={searchQuery.searchValue} key={index} />
          )}
        />
      )}
      {paginator && (
        <Pagination
          count={paginator?.totalElements}
          onChangePage={(page) => setCurrentPage(page)}
          current={currentPage}
          perPage={perPage}
          onChangePerPage={(value) => setPerPage(value)}
          isLoading={isPaginationLoading}
        />
      )}
    </Box>
  );
};

export default DictionariesPage;
